function asRecord(x: unknown): Record<string, unknown> | null {
  if (x !== null && typeof x === "object") return x as Record<string, unknown>;
  return null;
}

function stripExecutionReverted(msg: string): string {
  return msg.replace(/^execution reverted:?\s*/i, "").trim();
}

/**
 * Pulls the on-chain revert reason (or closest RPC message) from ethers v5 /
 * MetaMask / JSON-RPC error shapes so users see the contract message, not only
 * UNPREDICTABLE_GAS_LIMIT.
 */
export function getContractErrorMessage(err: unknown): string {
  if (typeof err === "string" && err.trim()) return err.trim();

  const visited = new WeakSet<object>();

  const fromMessageString = (message: string): string | null => {
    const reasonMatch = message.match(/reason="([^"]+)"/);
    if (reasonMatch?.[1]) {
      const inner = stripExecutionReverted(reasonMatch[1]);
      if (inner) return inner;
    }
    const execMatch = message.match(/execution reverted:?\s*([^"(\n]+?)(?:\s*\(|$|\n)/i);
    if (execMatch?.[1]) {
      const inner = execMatch[1].trim();
      if (inner && !inner.includes("UNPREDICTABLE_GAS_LIMIT")) return inner;
    }
    return null;
  };

  const walk = (e: unknown): string | null => {
    const o = asRecord(e);
    if (!o) return null;
    if (visited.has(o)) return null;
    visited.add(o);

    const data = asRecord(o.data);
    if (data && typeof data.message === "string") {
      const stripped = stripExecutionReverted(data.message);
      if (
        stripped &&
        stripped !== "Internal JSON-RPC error." &&
        stripped !== "Internal JSON-RPC error"
      ) {
        return stripped;
      }
    }

    if (typeof o.reason === "string" && o.reason.trim()) {
      return stripExecutionReverted(o.reason);
    }

    if (typeof o.message === "string" && o.message.trim()) {
      const fromNested = fromMessageString(o.message);
      if (fromNested) return fromNested;
      if (
        !o.message.includes("UNPREDICTABLE_GAS_LIMIT") &&
        !o.message.includes("cannot estimate gas")
      ) {
        return o.message.trim();
      }
    }

    if (o.error !== undefined) return walk(o.error);

    return null;
  };

  return walk(err) ?? "The transaction could not be completed.";
}

/** Short, user-facing guidance keyed off common revert strings. */
export function getContractErrorActionHint(contractMessage: string): string {
  const m = contractMessage.toLowerCase();

  if (m.includes("not whitelisted") || m.includes("whitelist")) {
    return "If you were recently added to the list, the pool owner must update the Merkle root on the contract so it matches the app whitelist. Otherwise confirm this wallet is whitelisted and you are connected to the correct network.";
  }
  if (m.includes("transfer amount exceeds balance") || m.includes("exceeds balance")) {
    return "You do not have enough of this token in your wallet for this action.";
  }
  if (
    m.includes("allowance") ||
    m.includes("insufficient allowance") ||
    m.includes("erc20: transfer amount exceeds allowance")
  ) {
    return "Approve the pool contract to spend your staking token, then try again.";
  }
  if (m.includes("harvest") || m.includes("too soon") || m.includes("cooldown")) {
    return "You may need to wait until the next harvest window; check the pool rules or try again later.";
  }

  return "Check that you are on the correct network, have enough native currency for gas, and that amounts and approvals are correct. Your wallet may show more detail.";
}
